import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

const SOURCE_DEFAULTS = {
  email: {
    type: 'email',
    label: 'School',
    icon: EmailOutlinedIcon,
  },
  sms: {
    type: 'sms',
    label: 'Text',
    icon: SmsOutlinedIcon,
  },
  manual: {
    type: 'manual',
    label: 'Manual',
    icon: EditNoteOutlinedIcon,
  },
  import: {
    type: 'import',
    label: 'Import',
    icon: UploadFileOutlinedIcon,
  },
  other: {
    type: 'other',
    label: 'Source',
    icon: OpenInNewOutlinedIcon,
  },
};

const CONFIDENCE_ORDER = {
  low: 0,
  medium: 1,
  high: 2,
};

export const SOURCE_VERIFY_CONFIDENCE_THRESHOLD = 'medium';

const normalizeSourceType = (source = {}) => {
  const candidate = String(
    source?.type
    || source?.sourceType
    || source?.channel
    || ''
  ).trim().toLowerCase();

  if (SOURCE_DEFAULTS[candidate]) {
    return candidate;
  }

  if (candidate.includes('email')) return 'email';
  if (candidate.includes('sms') || candidate.includes('text')) return 'sms';
  if (candidate.includes('import')) return 'import';
  if (candidate.includes('manual')) return 'manual';

  return 'other';
};

export const normalizeTimelineSourceConfidence = (value) => {
  const candidate = String(value || '').trim().toLowerCase();
  if (candidate in CONFIDENCE_ORDER) {
    return candidate;
  }

  return null;
};

export const getTimelineSourceConfidenceRank = (value) => {
  const normalized = normalizeTimelineSourceConfidence(value);
  if (!normalized) {
    return null;
  }

  return CONFIDENCE_ORDER[normalized];
};

export const shouldShowTimelineSourceReview = (source = {}) => {
  const sourceType = normalizeSourceType(source);
  if (sourceType !== 'email') {
    return false;
  }

  if (source?.verified === true || source?.reviewedAt) {
    return false;
  }

  const confidenceRank = getTimelineSourceConfidenceRank(source?.confidence);
  const thresholdRank = CONFIDENCE_ORDER[SOURCE_VERIFY_CONFIDENCE_THRESHOLD];

  if (confidenceRank == null) {
    return false;
  }

  return confidenceRank < thresholdRank;
};

export const getTimelineSourceMeta = (source = {}, fallback = {}) => {
  const sourceType = normalizeSourceType(source);
  const defaults = SOURCE_DEFAULTS[sourceType] || SOURCE_DEFAULTS.other;
  const senderName = String(source?.senderName || fallback?.senderName || '').trim();
  const label = senderName || defaults.label;
  const confidence = normalizeTimelineSourceConfidence(source?.confidence || fallback?.confidence);
  const confidenceRank = confidence != null ? CONFIDENCE_ORDER[confidence] : null;
  const verified = source?.verified === true || fallback?.verified === true;
  const reviewedAt = source?.reviewedAt || fallback?.reviewedAt || null;
  const reviewedBy = source?.reviewedBy || fallback?.reviewedBy || null;

  return {
    type: sourceType,
    label,
    iconComponent: defaults.icon,
    icon: null,
    senderName: senderName || null,
    senderEmail: source?.senderEmail || fallback?.senderEmail || null,
    subject: source?.subject || fallback?.subject || null,
    body: source?.body || source?.originalBody || fallback?.body || null,
    receivedAt: source?.receivedAt || fallback?.receivedAt || null,
    messageId: source?.messageId || fallback?.messageId || null,
    extractedBy: source?.extractedBy || fallback?.extractedBy || null,
    confidence: confidence || source?.confidence || fallback?.confidence || null,
    confidenceRank,
    verified,
    reviewedAt,
    reviewedBy,
    needsVerification: shouldShowTimelineSourceReview({
      ...source,
      type: sourceType,
      confidence: confidence || source?.confidence || fallback?.confidence || null,
      verified,
      reviewedAt,
      reviewedBy,
    }),
    displayLabel: label,
    isEmail: sourceType === 'email',
  };
};

export default getTimelineSourceMeta;
